// src/features/landing/application/useJobPosts.js
import { useState, useEffect, useCallback } from 'react';
import { jobPostRepository } from '../infrastructure/jobPostApiRepository';

/**
 * @typedef {Object} Filters
 * @property {string} [companySido]
 * @property {string} [jobType]
 * @property {string} [sort]
 */

/**
 * @typedef {Object} UseJobPostsResult
 * @property {import("../domain/jobPost").JobPost[]} jobPosts
 * @property {boolean} loading
 * @property {string|null} error
 * @property {number} totalPages
 * @property {number} totalElements
 * @property {number} currentPage
 * @property {Filters} filters
 * @property {function(number): void} setPage
 * @property {function(Filters): void} setFilters
 * @property {function(): void} refresh
 */

/**
 * @param {number} initialSize
 * @param {number} initialPage
 * @param {Filters} initialFilters
 * @returns {UseJobPostsResult}
 */
export function useJobPosts(initialSize = 8, initialPage = 0, initialFilters = {}) {
    const [jobPosts, setJobPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [size] = useState(initialSize);
    const [filters, setFiltersState] = useState(initialFilters);

    const fetchJobPosts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await jobPostRepository.getJobPosts(currentPage, size, filters);
            setJobPosts(response.content);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
        } catch (err) {
            setError(err.message);
            setJobPosts([]);
        } finally {
            setLoading(false);
        }
    }, [currentPage, size, filters]);

    useEffect(() => {
        fetchJobPosts();
    }, [fetchJobPosts]);

    useEffect(() => {
        setCurrentPage(initialPage);
    }, [initialPage]);

    const setPage = useCallback((page) => {
        setCurrentPage(page);
    }, []);

    const setFilters = useCallback((newFilters) => {
        setFiltersState(newFilters);
        setCurrentPage(0);
    }, []);

    const refresh = useCallback(() => {
        fetchJobPosts();
    }, [fetchJobPosts]);

    return {
        jobPosts,
        loading,
        error,
        totalPages,
        totalElements,
        currentPage,
        filters,
        setPage,
        setFilters,
        refresh,
    };
}
